"use client";

import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { LottieAnimation } from "@/components/common";
import { HeroBtnGroup } from "@/components/common";
import { AnimatePresence, motion } from "framer-motion";

const rotatingBusinessTypes = [
  "Retail Business",
  "Local Retail",
  "Ecommerce",
  "D2C Brands",
  "Restaurants",
  "Pharmacies",
  "Salons",
  "Fashion Stores",
  "Grocery Stores",
];

export interface HeroSectionProps {
  badgeText?: string;
  title?: string;
  gradientSubHeading?: string;
  baseSubHeading?: string;
  microSubHeading?: string;
  lottiePhoneAnimation?: ILottieAnimation["animation"];
  lottieBackgroundAnimation?: ILottieAnimation["animation"];
  showButtons?: boolean;
  className?: string;
  supportingText: string;
  primaryButton?: {
    title: string;
    link: string;
  };
  secondaryButton?: {
    title: string;
    link: string;
  };
}

function RotatingBusinessType({
  businessType,
  className,
}: {
  businessType: string;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={`relative inline-block h-[1.16em] overflow-hidden align-[-0.08em] text-transparent ${className ?? ""}`}
    >
      <span className="invisible block whitespace-nowrap">
        {businessType}
      </span>
      <AnimatePresence initial={false} mode="popLayout">
        <motion.span
          key={businessType}
          className="absolute left-0 top-0 flex h-full w-full items-center justify-center whitespace-nowrap text-brand-gradient"
          initial={{ opacity: 0, y: "100%" }}
          animate={{ opacity: 1, y: "0%" }}
          exit={{ opacity: 0, y: "-100%" }}
          transition={{ duration: 0.28, ease: "easeOut" }}
        >
          {businessType}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

const HomeHeroSection: React.FC<HeroSectionProps> = ({
  badgeText = "Trusted by 1000+ merchants",
  title = "Title",
  gradientSubHeading = "",
  baseSubHeading = "",
  microSubHeading = "",
  lottiePhoneAnimation = "MERCHANT_BANNER_PHONE",
  lottieBackgroundAnimation = "MERCHANT_BANNER_WITHOUT_PHONE",
  supportingText,
  showButtons = true,
  className = "",
  primaryButton,
  secondaryButton,
}) => {
  const [activeBusinessType, setActiveBusinessType] = useState(0);
  const shouldRotateBusinessType =
    title === "Increase Repeat Customers for Your Retail Business";

  useEffect(() => {
    if (!shouldRotateBusinessType) {
      return;
    }

    const timer = window.setInterval(() => {
      setActiveBusinessType(
        (businessType) => (businessType + 1) % rotatingBusinessTypes.length
      );
    }, 1000);

    return () => window.clearInterval(timer);
  }, [shouldRotateBusinessType]);

  return (
    <section
      className={`bg-secondary-2 text-secondary-foreground xl:h-[calc(100vh-62px)] overflow-hidden ${className}`}
    >
      <div className="scontainer h-full pt-12 xl:py-0 flex xl:flex-row flex-col items-center xl:items-start overflow-hidden relative">
        {/* Text Section */}
        <div className="flex flex-col items-center xl:items-start mx-auto xl:mx-0 gap-6 xl:w-[40vw] h-full xl:pb-12 pb-0 justify-center sm:px-0">
          <Badge variant="metal-gray-gradient">
            <Star className="w-4 h-4 fill-white" />
            <span className="font-bold text-[12px]">{badgeText}</span>
          </Badge>

          <h1 className="xl:text-start text-center max-w-[40rem] md:max-w-[45rem] lg:max-w-[40rem] xl:max-w-[32rem] sm:px-0 px-2">
            {shouldRotateBusinessType ? (
              <>
                <span className="sr-only">
                  Increase Repeat Customers for Your{" "}
                  {rotatingBusinessTypes[activeBusinessType]}
                </span>
                <span aria-hidden="true" className="block sm:hidden">
                  Increase Repeat
                </span>
                <span aria-hidden="true" className="block sm:hidden">
                  Customers for Your
                </span>
                <span aria-hidden="true" className="block sm:hidden">
                  <RotatingBusinessType
                    businessType={rotatingBusinessTypes[activeBusinessType]}
                  />
                </span>
                <span aria-hidden="true" className="hidden sm:inline">
                  Increase Repeat Customers{" "}
                  {/* The h1's own max-width narrows to 32rem at xl (to make
                      room for the phone animation) while its font-size stays
                      46px - too tight for the longest rotating words ("Grocery
                      Stores", "D2C Brands", "Retail Business") to keep
                      "for Your <word>" on one line, so this line alone steps
                      down in size at xl instead of overflowing. */}
                  <span className="whitespace-nowrap xl:text-[36px]">
                    for Your{" "}
                    <RotatingBusinessType
                      businessType={rotatingBusinessTypes[activeBusinessType]}
                    />
                  </span>
                </span>
              </>
            ) : (
              title
            )}
          </h1>

          <p className="font-semibold max-w-[23rem] sm:max-w-[30rem] text-xl xl:text-start text-center">
            <span className="font-normal text-[#CCCCCC]">{gradientSubHeading}</span>{" "}
            <span className="text-brand-gradient">
              {baseSubHeading}
            </span>
          </p>

          <p className="font-semibold max-w-[23rem] sm:max-w-[30rem] xl:text-start text-center">
            {microSubHeading}
          </p>

          {showButtons && (
            <HeroBtnGroup
              primaryButton={primaryButton}
              secondaryButton={secondaryButton}
              className="w-[65%]"
            />
          )}

          <p className="font-semibold max-w-[23rem] sm:max-w-[30rem] xl:text-start text-center">
            {supportingText}
          </p>
        </div>

        {/* Animation Section */}
        <div className="xl:w-[44rem] xl:h-[35rem] sm:h-[37rem] sm:w-[40rem] w-[98vw] h-[90vw] relative xl:self-end self-center">
          <LottieAnimation
            className="absolute xl:right-0 -bottom-0 w-full h-full"
            animation={lottiePhoneAnimation}
            loop={false}
          />
          <LottieAnimation
            className="absolute xl:right-0 -bottom-0 w-full h-full"
            animation={lottieBackgroundAnimation}
            loop={true}
          />
        </div>
      </div>
    </section>
  );
};

export default HomeHeroSection;
